import React from "react";
import { CheckOutlined } from "@ant-design/icons";

import styles from "./WizardStepper.module.css";

export interface WizardStep {
  id: string;
  label: string;
}

export interface WizardStepperProps {
  steps: ReadonlyArray<WizardStep>;
  activeStepId: string;
  /** Step ids already submitted. Order doesn't matter — completion is sticky and idempotent. */
  completedStepIds?: ReadonlyArray<string>;
  /** Parent decides whether to honor the click. We also disable buttons we know are off-limits so keyboard a11y stays honest. */
  onStepClick?: (stepId: string) => void;
  /** When true, every button is disabled. Used post-submit when the wizard is read-only. */
  locked?: boolean;
  /** Prefix for data-testid and aria-controls so multiple wizards on a page don't collide. */
  idPrefix: string;
  ariaLabel: string;
}

function WizardStepper({
  steps,
  activeStepId,
  completedStepIds = [],
  onStepClick,
  locked = false,
  idPrefix,
  ariaLabel,
}: WizardStepperProps) {
  const completedSet = new Set(completedStepIds);

  const handleClick = (stepId: string) => {
    if (locked) return;
    onStepClick?.(stepId);
  };

  return (
    <div
      className={styles.steps}
      role="tablist"
      aria-label={ariaLabel}
      data-testid={`${idPrefix}s`}
    >
      {steps.map((step, idx) => {
        const isActive = step.id === activeStepId;
        const isCompleted = completedSet.has(step.id);
        // Clickable rule: the active step is always "clickable" (no-op click anyway),
        // a completed step can be revisited, future uncompleted steps are blocked.
        // `locked` overrides everything.
        const isClickable = !locked && (isActive || isCompleted);

        // active styling wins over completed styling when both apply, so the user
        // sees an orange ring while editing a previously-completed step instead
        // of a frozen-looking green check.
        const buttonClassName = [
          styles.step,
          isCompleted ? styles.stepCompleted : "",
          isActive ? styles.stepActive : "",
        ]
          .filter(Boolean)
          .join(" ");

        const numberClassName = [
          styles.stepNumber,
          isCompleted ? styles.stepNumberCompleted : "",
          isActive ? styles.stepNumberActive : "",
        ]
          .filter(Boolean)
          .join(" ");

        const showCheck = isCompleted && !isActive;

        return (
          <button
            key={step.id}
            type="button"
            role="tab"
            id={`${idPrefix}-${step.id}`}
            aria-selected={isActive}
            aria-controls={`${idPrefix}-panel-${step.id}`}
            aria-disabled={!isClickable}
            disabled={!isClickable}
            tabIndex={isActive ? 0 : -1}
            className={buttonClassName}
            onClick={() => handleClick(step.id)}
            data-testid={`${idPrefix}-${step.id}`}
          >
            <span className={numberClassName} aria-hidden="true">
              {showCheck ? (
                <CheckOutlined
                  className={styles.stepCheckIcon}
                  data-testid={`${idPrefix}-${step.id}-check`}
                />
              ) : (
                idx + 1
              )}
            </span>
            <span className={styles.stepLabel}>{step.label}</span>
          </button>
        );
      })}
    </div>
  );
}

export default WizardStepper;
